import React, { useRef, useMemo, useCallback } from "react";
import styled from "styled-components";
import { Icon } from "assets/Icons_";
import { active_project } from "state/store.global";
import { getFocus, useGet } from "state/jotai";
import { _project } from "state/store.projects";
import { BREAK_ } from "constants";
import { darken } from "polished";


// Helper functions for color logic
const getBackgroundColor = (variant, clr, pin, type, isIsometry, isMobile, backgroundColor) => {
  if (variant === "tertiary") return "transparent";
  if (variant === "secondary-1st-varient") return "#ffffffbf";
  if (backgroundColor) return backgroundColor;
  
  if (type === "poly" && pin === 0 && isIsometry) {
    return isMobile && variant === "primary" ? clr?.primary : clr?.buttons_text;
  }
  
  if (type === "mono" && pin === 0 && isIsometry) {
    return isMobile && variant === "secondary" ? clr?.buttons_text : clr?.primary;
  }
  
  if (type === "poly" && pin !== 0 && isIsometry) {
    return isMobile && variant === "secondary" ? clr?.buttons_text : clr?.primary;
  }
  
  return variant === "primary" ? clr?.primary : clr?.buttons_text;
};

const getHoverBackgroundColor = (variant, clr, pin, type, isIsometry, isMobile, backgroundColor) => {
  if (variant === "tertiary") return "white";
  if (variant === "secondary-1st-varient") return "#FFFFFF";
  if (backgroundColor) return darken(0.04, backgroundColor);
  
  const baseColor = getBackgroundColor(variant, clr, pin, type, isIsometry, isMobile, null);
  return darken(0.04, baseColor);
};

const getFocusBackgroundColor = (variant, clr, pin, type, isIsometry, isMobile, isStatus, backgroundColor) => {
  if (isStatus) return null;
  if (variant === "tertiary") return "white";
  if (variant === "secondary-1st-varient") return "#ffffff";
  if (backgroundColor) return darken(0.04, backgroundColor);
  
  if (type === "poly" && pin === 0 && isIsometry) {
    return isMobile && variant === "primary" ? darken(0.04, clr?.primary) : clr?.buttons_text;
  }
  
  if (type === "mono" && pin === 0 && isIsometry) {
    return isMobile && variant === "secondary" ? clr?.buttons_text : darken(0.04, clr?.primary);
  }
  
  return variant === "primary" ? darken(0.04, clr?.primary) : clr?.buttons_text;
};

export const ETStyledButton = styled.button`
  display: flex;
  justify-content: center;
  align-items: center;
  cursor: pointer;
  gap: 8px;
  box-sizing: border-box;
  height: 40px;
  
  border-radius: ${({ size, borderRadius }) => borderRadius || (size === "sm" ? "4px" : "8px")};
  padding: ${({ padding }) => padding || "12px 40px"};
  line-height: 1;
  user-select: none;
  white-space: nowrap;

  ${BREAK_.md_up} {
    height: 40px;
    width: ${({ width }) => width || '185px'};
  }

  background-color: ${({ variant, clr, pin, type, isIsometry, isMobile, backgroundColor }) => 
    getBackgroundColor(variant, clr, pin, type, isIsometry, isMobile, backgroundColor)};

  color: ${({ backgroundColor, variant }) =>
    backgroundColor === "gray" || variant === "secondary" || variant === "secondary-1st-varient" ? "#000000" : "#ffffff"};

  transition: background-color 0.1s ease-in, border 0.1s ease-in, box-shadow 0.1s ease-in, color 0.1s ease-in;

  border: ${({ variant }) => {
    if (variant === "tertiary") return "1px solid white";
    if (variant === "secondary-1st-varient") return "none";
    if (variant === "primary") return "none";
    return "1px solid #D1D1D1";
  }};

  &:hover {
    background-color: ${({ variant, clr, pin, type, isIsometry, isMobile, backgroundColor }) => 
      getHoverBackgroundColor(variant, clr, pin, type, isIsometry, isMobile, backgroundColor)};
    
    svg {
      color: ${({ variant }) => (variant === "tertiary" || variant === "secondary-1st-varient" ? "#000000" : "#ffffff")};
    }
    
    border: ${({ variant }) =>
      variant === "tertiary" || variant === "primary" || variant === "secondary-1st-varient" ? "none" : "1px solid #D1D1D1"};
  }

  &:focus {
    background-color: ${({ variant, clr, pin, type, isIsometry, isMobile, isStatus, backgroundColor }) => 
      getFocusBackgroundColor(variant, clr, pin, type, isIsometry, isMobile, isStatus, backgroundColor)};
    
    svg {
      color: ${({ variant, skipColorChange }) => ((variant === "tertiary" || variant === "secondary-1st-varient") && !skipColorChange ? "#000000" : "#ffffff")};
    }
    
    box-shadow: ${({ variant, clr, isStatus, boxShadow }) => {
      if (variant === "tertiary") return `0 0 0 1.5px #D1D1D1`;
      if (variant === "primary" && !isStatus) return `0 0 0 1.5px ${clr?.primary}`;
      if (variant === "secondary" && !isStatus) return `0 0 0 1.5px #D1D1D1`;
      if (variant === "secondary-1st-varient" && !boxShadow) return "0 0 0 1.5px #FFFFFFbf";
      return "none";
    }};
    
    border: ${({ variant }) => 
      (variant === "tertiary" || variant === "secondary-1st-varient" || variant === "primary") ? "none" : ""};
  }

  &:disabled {
    cursor: not-allowed;
    opacity: 0.5;
  }
`;

export default function EusButton({
  width,
  backgroundColor,
  onClick = () => {},
  buttonText,
  otherText,
  icon,
  fontSize,
  rotate,
  isFaIcon,
  iconColor,
  disabled,
  padding,
  iconSize,
  type,
  variant,
  height,
  iconAlign,
  size,
  iconName,
  clr,
  borderRadius,
  buttonlabel,
  skipColorChange,
  boxShadow
}) {
  const buttonRef = useRef(null);
  const { pin, pid } = useGet(active_project);
  const buttonsLabel = getFocus(_project, "buttonsLabel")?.[pin]?.[pid] ?? [];

  const buttonTextContent = useMemo(() => {
    if (buttonlabel && buttonText) {
      const foundLabel = buttonsLabel.find(btn => btn?.UILabelProperty === buttonText);
      return foundLabel?.Label || otherText;
    }
    return otherText;
  }, [buttonlabel, buttonText, buttonsLabel, otherText]);

  const handleClick = useCallback(() => {
    onClick();
    if (buttonRef?.current) {
      buttonRef.current.blur();
    }
  }, [onClick]);

  const iconProps = useMemo(() => ({
    color: iconColor || clr?.primary_text,
    icon: iconName,
    isFaIcon,
    size: isFaIcon ? (iconSize || "lg") : size,
    skipColorChange
  }), [iconColor, clr?.primary_text, iconName, isFaIcon, iconSize, size, skipColorChange]);

  return (
    <ETStyledButton
      ref={buttonRef}
      width={width}
      disabled={disabled}
      onClick={handleClick}
      borderRadius={borderRadius}
      variant={variant}
      clr={clr}
      padding={padding}
      type={type}
      size={size}
      backgroundColor={backgroundColor}
      height={height}
      fontSize={fontSize}
      boxShadow={boxShadow}
    >
      {icon && iconAlign === "left" && <Icon {...iconProps} />}
      <span className="eus-button-2">{buttonTextContent}</span>
      {icon && iconAlign === "right" && <Icon {...iconProps} />}
    </ETStyledButton>
  );
}

EusButton.defaultProps = {
  disabled: false,
  type: "button",
  iconAlign: "left",
};
